import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminAssetRowSchema, assetTypeOptionSchema } from './assets.screen.js'

/*
 * «Activos» › the form of an asset (`admin.assets.form`, create and edit) and the CSV import (`admin.assets.import`).
 * The form needs the asset being edited and the three catalogues of its dropdowns; the import needs the types and
 * categories a row is checked against. The networks come with the effective address pattern of each one, so the form
 * validates with the rule the server applies.
 */

export const assetCategoryOptionSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  assetTypeKey: z.string().nullable(),
  sortOrder: z.number(),
})

export const assetChainNetworkOptionSchema = z.strictObject({
  chainId: z.number().int(),
  slug: z.string(),
  displayName: z.string(),
  /** Effective address regex of the chain (its own, or the default of its VM family), or null. */
  addressPattern: z.string().nullable(),
  /** A real address of the chain, for the placeholder of the input, or null. */
  addressExample: z.string().nullable(),
})

export const adminAssetFormScreen = defineScreen({
  id: 'admin.assets.form',
  path: '/api/screens/admin/assets/form',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid().optional() }),
  response: z.strictObject({
    /** The asset being edited; `null` when creating. */
    asset: adminAssetRowSchema.nullable(),
    types: screenSection(z.array(assetTypeOptionSchema)),
    categories: screenSection(z.array(assetCategoryOptionSchema)),
    chainNetworks: screenSection(z.array(assetChainNetworkOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.assets'],
  budget: { requests: 1, statements: 12, bytes: 30_000, p95Ms: 200 },
  summary:
    'Assets › form: the asset being edited (none when creating) and the three catalogues of its dropdowns',
})

export const adminAssetImportScreen = defineScreen({
  id: 'admin.assets.import',
  path: '/api/screens/admin/assets/import',
  access: 'super_admin',
  response: z.strictObject({
    types: screenSection(z.array(assetTypeOptionSchema)),
    categories: screenSection(z.array(assetCategoryOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.assets'],
  budget: { requests: 1, statements: 6, bytes: 8_000, p95Ms: 100 },
  summary: 'Assets › CSV import: the asset types and categories a row is checked against',
})

export type AssetCategoryOption = z.infer<typeof assetCategoryOptionSchema>
export type AssetChainNetworkOption = z.infer<typeof assetChainNetworkOptionSchema>
export type AdminAssetFormData = ScreenData<typeof adminAssetFormScreen>
export type AdminAssetImportData = ScreenData<typeof adminAssetImportScreen>
